import { Button } from "src/components";
import { EstateItemType } from "../../forms/EstateItemType";
import DiscardModal from "../../../components/Modal/DiscardModal/DiscardModal";
import React, { useEffect, useState } from "react";
import { estateFlowchartApiClient } from "src/EstateFlowchart/client/EstateFlowchartApiClient";
import { useHistory, useParams } from "react-router-dom";
import { RouteWithIdAndFlowchartId } from "../../../routes/types";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import {
    deleteEstateFlowchart,
    deleteIrrevocableTrust,
    deleteMiscellaneousTrust,
    deleteRevocableTrust,
    deleteWill,
    getEstateFlowchartsSelector,
    setGenericErrorModal
} from "src/EstateFlowchart/slices/EstateFlowChartSlice";
import { EstateFlowchartState, EstateItem } from "../../model/EstateFlowchartTypes";
import { setActiveNavItem } from "../../slices/NavigationSlice";
import { selectReleaseToggles } from "../../../ReleaseToggles/releaseTogglesSlice";

type DeleteButtonProps = {
    estateItemType: EstateItemType;
    estateItem?: EstateItem;
};

const deleteButtonLabels = {
    [EstateItemType.REVOCABLE_TRUST]: "Revocable Trust",
    [EstateItemType.IRREVOCABLE_TRUST]: "Irrevocable Trust",
    [EstateItemType.WILL]: "Will",
    [EstateItemType.MISCELLANEOUS]: "Miscellaneous"
};

export default function DeleteButton({ estateItemType, estateItem }: Readonly<DeleteButtonProps>) {
    const hasId = estateItem?.trustId !== "";
    const { id, flowchartId, trustId } = useParams<RouteWithIdAndFlowchartId>();
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedFlowchart, setSelectedFlowchart] = useState<EstateFlowchartState>()
    const history = useHistory();
    const dispatch = useAppDispatch();

    const releaseToggles = useAppSelector(selectReleaseToggles);
    const estateFlowcharts = useAppSelector(getEstateFlowchartsSelector);

    const message = estateItemType === EstateItemType.WILL
        ? "This Will and its associated data will be deleted permanently."
        : `This ${deleteButtonLabels[estateItemType]} and its associated distributions will be deleted permanently.`;

    useEffect(() => {
        const selectedFlowchart = estateFlowcharts.find(fc => fc.flowchartId === flowchartId);
        setSelectedFlowchart(selectedFlowchart);
    }, [estateFlowcharts, flowchartId]);

    if (!hasId) return null;

    const handleDiscardChanges = () => {
        if ((hasOnlyRevTrust || hasOnlyWill || hasOnlyIrrevTrust || hasOnlyMiscellaneous) && flowchartId) {
            handleDeleteFlowchart(flowchartId);
        } else if (estateItemType === EstateItemType.REVOCABLE_TRUST && trustId) {
            handleDeleteRevocableTrust(trustId, releaseToggles?.enableEstateFlowchartMVPRelease);
        } else if (estateItemType === EstateItemType.WILL && trustId) {
            handleDeleteWill(trustId);
        } else if (estateItemType === EstateItemType.IRREVOCABLE_TRUST && trustId) {
            handleDeleteIrrevocableTrust(trustId, releaseToggles?.enableEstateFlowchartMVPRelease);
        } else if (estateItemType === EstateItemType.MISCELLANEOUS && trustId) {
            handleDeleteMiscellaneous(trustId);
        }
        setDeleteModal(false);
        dispatch(setActiveNavItem(undefined));
        history.push(`/Profile/${id}/ClientProfile/EstateFlowchart`);
    }

    const hasOnlyRevTrust = !!selectedFlowchart?.revocableTrust &&
        (!selectedFlowchart?.willSummary?.primaryWill && !selectedFlowchart?.willSummary?.partnerWill) &&
        (selectedFlowchart?.irrevocableTrusts?.length === 0 || selectedFlowchart?.irrevocableTrusts === undefined) &&
        (!selectedFlowchart?.wildcardTrusts || selectedFlowchart?.wildcardTrusts.length === 0);

    const hasOnlyWill = (
        (
            (selectedFlowchart?.willSummary?.primaryWill && !selectedFlowchart?.willSummary?.partnerWill) ||
            (!selectedFlowchart?.willSummary?.primaryWill && selectedFlowchart?.willSummary?.partnerWill)
        )
        && !selectedFlowchart?.revocableTrust
        && (selectedFlowchart?.irrevocableTrusts?.length === 0 || selectedFlowchart?.irrevocableTrusts === undefined)
        && (!selectedFlowchart?.wildcardTrusts || selectedFlowchart?.wildcardTrusts.length === 0)
    );

    const hasOnlyIrrevTrust = selectedFlowchart?.irrevocableTrusts?.length === 1 &&
        (!selectedFlowchart?.revocableTrust) &&
        (!selectedFlowchart?.willSummary?.primaryWill && !selectedFlowchart?.willSummary?.partnerWill) &&
        (!selectedFlowchart?.wildcardTrusts || selectedFlowchart?.wildcardTrusts.length === 0);


    // Only Miscellaneous left in flowchart
    const hasOnlyMiscellaneous = (
        selectedFlowchart?.wildcardTrusts?.length === 1 &&
        (!selectedFlowchart?.revocableTrust) &&
        (!selectedFlowchart?.willSummary?.primaryWill && !selectedFlowchart?.willSummary?.partnerWill) &&
        (!selectedFlowchart?.irrevocableTrusts || selectedFlowchart?.irrevocableTrusts.length === 0)
    );

    const setCommunicationError = (errorStatus: any) => {
        dispatch(setGenericErrorModal({
            isOpen: true,
            operationId: errorStatus.traceId
        }));
    }

    const handleDeleteFlowchart = (flowchartId: string) => {
        estateFlowchartApiClient.deleteFlowchart(id, flowchartId)
            .then((response) => {
                if (!response?.isSuccessStatusCode) {
                    if (releaseToggles?.enableEstateFlowchartMVPRelease) {
                        setCommunicationError(response);
                    }
                } else {
                    dispatch(deleteEstateFlowchart(flowchartId));
                }
            });
    }

    const handleDeleteRevocableTrust = (revocableTrustId: string, enableEstateFlowchartMVPRelease: boolean | undefined) => {
        estateFlowchartApiClient.deleteRevocableTrust(id, revocableTrustId)
            .then((response) => {
                if (!response?.isSuccessStatusCode) {
                    if (enableEstateFlowchartMVPRelease) {
                        setCommunicationError(response);
                    }
                } else if (flowchartId && revocableTrustId) {
                    dispatch(deleteRevocableTrust({ flowchartId, trustId: revocableTrustId }));
                }
            });
    }

    const handleDeleteIrrevocableTrust = (irrevocableTrustId: string, enableEstateFlowchartMVPRelease: boolean | undefined) => {
        estateFlowchartApiClient.deleteIrrevocableTrust(id, irrevocableTrustId)
            .then((res) => {
                if (!res?.isSuccessStatusCode) {
                    if (enableEstateFlowchartMVPRelease) {
                        setCommunicationError(res);
                    }
                } else if (flowchartId && irrevocableTrustId) {
                    dispatch(deleteIrrevocableTrust({ flowchartId, trustId: irrevocableTrustId }));
                }
            });
    }

    const handleDeleteWill = (willId: string) => {
        estateFlowchartApiClient.deleteWill(id, willId)
            .then((response) => {
                if (!response?.isSuccessStatusCode) {
                    setCommunicationError(response);
                } else if (flowchartId && willId) {
                    dispatch(deleteWill({ flowchartId, trustId: willId }));
                }
            });
    }

    const handleDeleteMiscellaneous = (miscTrustId: string) => {
        estateFlowchartApiClient.deleteMiscellaneous(id, miscTrustId)
            .then((response) => {
                if (!response?.isSuccessStatusCode) {
                    setCommunicationError(response);
                } else if (flowchartId && miscTrustId) {
                    dispatch(deleteMiscellaneousTrust({flowchartId, trustId: miscTrustId}));
                }
            });
    }

    return (
        <>
            <Button
                className="marginright-sm"
                icon="none"
                kind="secondary"
                onClick={() => setDeleteModal(true)}
                size="medium"
                type="button"
                style={{ backgroundColor: "#D04532", color: "#fff", border: "none", marginRight: "8px" }}
            >
                {`Delete ${deleteButtonLabels[estateItemType]}`}
            </Button>
            <DiscardModal
                isOpen={deleteModal}
                onClickDiscardChanges={() => handleDiscardChanges()}
                onClickKeepEditing={() => {
                    setDeleteModal(false)
                }}
                isEditing
                message={message}
                headerText={`Delete ${deleteButtonLabels[estateItemType]}?`}
                keepEditingText="Cancel"
                discardChangesText="Delete"
            />
        </>
    );
}